import { genName } from '@/components/Subregions/utils';

export const PRIMARY = 'Primary';
const ADDITIONAL = 'Additional';

// Primary ranks the final-mile carrier; the middle-mile and warehouse slots of that row are not.
const isPrimaryFor = (mapping, carrierId) => mapping.is_primary && mapping.carrier_id === carrierId;

// The carrier's Regions page settings that show as badges, in order.
const ZONE_LABELS = [
    ['is_resource_zone', 'Resource Zone'],
    ['is_pricing_zone', 'Pricing Zone'],
];

export const coverageSummary = ({ ownZips, resources, subregions }) => {
    const zipCount = ownZips.aggregate.count;

    return {
        zipCount,
        subregionCount: subregions.length,
        resourceCount: resources.length,
        isSubmitted: zipCount > 0,
    };
};

// The receiving warehouse's name, as the Regions page shows it. Null when the sub-region uses the default.
const warehouseName = (warehouseId, locations) => {
    if (!warehouseId) return null;
    const index = locations.findIndex((location) => location.location_id === warehouseId);
    if (index === -1) return 'Unknown warehouse';
    return locations[index].location_name || `Warehouse ${index + 1}`;
};

/**
 * The carrier's own sub-regions from its Regions page, in query order: zip count, zone badges and warehouse.
 * `locations` is the carrier's; customer locations aren't warehouses.
 */
export const regionRows = (subregions, locations = []) => {
    const warehouses = locations.filter((location) => !location.is_customer_location);

    return subregions.map((subregion, idx) => ({
        subregion_id: subregion.subregion_id,
        name: genName(subregion, idx),
        zipCount: subregion.zips_aggregate.aggregate.count,
        zones: ZONE_LABELS.filter(([flag]) => subregion[flag]).map(([, label]) => label),
        warehouse: warehouseName(subregion.warehouse_id, warehouses),
    }));
};

export const profilePlacements = (deliveryProfiles, carrierId) =>
    deliveryProfiles.reduce((placements, profile) => {
        const roles = profile.subregions.reduce((rolesInProfile, subregion) => {
            subregion.carriers.forEach((mapping) => {
                const mappedRole = isPrimaryFor(mapping, carrierId) ? PRIMARY : ADDITIONAL;
                rolesInProfile.add(mappedRole);
            });

            return rolesInProfile;
        }, new Set());

        if (roles.size === 0) return placements;

        // A shipper holds one assignment per order type, so it can sit on the same profile twice.
        const shippersById = profile.auto_assignments.reduce((byId, assignment) => {
            byId.set(assignment.shipper_id, {
                shipper_id: assignment.shipper_id,
                business_name: assignment.shipper.business_name,
            });

            return byId;
        }, new Map());

        placements.push({
            delivery_profile_id: profile.delivery_profile_id,
            name: profile.name,
            roles: [PRIMARY, ADDITIONAL].filter((role) => roles.has(role)),
            shippers: [...shippersById.values()],
        });

        return placements;
    }, []);
